import { filterNames } from './filterNames';
import { serializes } from './serializes';
import { deserializes } from './deserializes';
import { getValueByPath } from '../../../helpers/getValueByPath';
import moment from 'moment';
import { searchDateFormat } from './common';
import { marksMap } from './marks';
import { dspxDiffDays } from '../../../helpers/dspx-utils';
import { statusChangePeriodsMap } from './statusChangePeriods';
import { expiredPeriodsMap } from './expiredPeriods';
import { anonymousApplicantData } from '../components/filters/ApplicantsFilter/consts';

const {
  createDate,
  statuses,
  companies,
  masters,
  executers,
  doneDate,
  parentServices,
  services,
  cities,
  regions,
  streets,
  houses,
  flats,
  marks,
  ratings,
  statusChangePeriod,
  expiredPeriod,
  creationMethods,
  priorities,
  createUsers,
  applicants,
  addressStagesLifeCycle,
} = filterNames;

const commonFilter = ({
  requests = [],
  values = [],
  key,
  valueType = 'number',
}) => {
  if (values.length === 0) {
    return requests;
  }

  const filteredRequests = requests.filter((request) => {
    const value = getValueByPath(request, key);

    const nullVal = valueType === 'string' ? '0' : 0;

    return values.includes(value ?? nullVal);
  });

  return filteredRequests;
};

export const filtersData = {
  [createDate]: {
    serialize: serializes.array,
    deserialize: deserializes.stringArray,
    name: createDate,
    defaultValue: [],
    filter: (requests) => requests,
  },
  [doneDate]: {
    serialize: serializes.array,
    deserialize: deserializes.stringArray,
    name: doneDate,
    defaultValue: [],
    filter: (requests = [], values = []) => {
      if (values.length < 2) {
        return requests;
      }

      const [fromDate, toDate] = values;

      const filteredRequests = requests.filter((request) => {
        const doneDateString = request.doneDate
          ? moment(request.doneDate).format(searchDateFormat)
          : undefined;

        return (
          doneDateString &&
          moment(doneDateString).isBetween(fromDate, toDate, undefined, '[]')
        );
      });

      return filteredRequests;
    },
  },
  [statuses]: {
    serialize: serializes.array,
    deserialize: deserializes.numberArray,
    name: statuses,
    defaultValue: [],
    filter: (requests = [], values = []) =>
      commonFilter({
        requests,
        values,
        key: 'statusId',
      }),
  },
  [companies]: {
    serialize: serializes.array,
    deserialize: deserializes.numberArray,
    name: companies,
    defaultValue: [],
    filter: (requests = [], values = []) =>
      commonFilter({
        requests,
        values,
        key: 'companyId',
      }),
  },
  [createUsers]: {
    serialize: serializes.array,
    deserialize: deserializes.numberArray,
    name: createUsers,
    defaultValue: [],
    filter: (requests = [], values = []) =>
      commonFilter({
        requests,
        values,
        key: 'createUser.id',
      }),
  },
  [masters]: {
    serialize: serializes.array,
    deserialize: deserializes.numberArray,
    name: masters,
    defaultValue: [],
    filter: (requests = [], values = []) =>
      commonFilter({
        requests,
        values,
        key: 'master.id',
      }),
  },
  [addressStagesLifeCycle]: {
    serialize: serializes.array,
    deserialize: deserializes.numberArray,
    name: addressStagesLifeCycle,
    defaultValue: [],
    filter: (requests = [], values = []) =>
      commonFilter({
        requests,
        values,
        key: 'stageAddressLifeCycle.id',
      }),
  },

  [executers]: {
    serialize: serializes.array,
    deserialize: deserializes.numberArray,
    name: executers,
    defaultValue: [],
    filter: (requests = [], values = []) =>
      commonFilter({
        requests,
        values,
        key: 'executer.id',
      }),
  },
  [parentServices]: {
    serialize: serializes.array,
    deserialize: deserializes.numberArray,
    name: parentServices,
    defaultValue: [],
    filter: (requests = [], values = []) =>
      commonFilter({
        requests,
        values,
        key: 'parentServiceId',
      }),
  },
  [services]: {
    serialize: serializes.array,
    deserialize: deserializes.numberArray,
    name: services,
    defaultValue: [],
    filter: (requests = [], values = []) =>
      commonFilter({
        requests,
        values,
        key: 'serviceId',
      }),
  },
  [cities]: {
    serialize: serializes.array,
    deserialize: deserializes.numberArray,
    name: cities,
    defaultValue: [],
    filter: (requests = [], values = []) =>
      commonFilter({
        requests,
        values,
        key: 'cityId',
      }),
  },
  [regions]: {
    serialize: serializes.array,
    deserialize: deserializes.numberArray,
    name: regions,
    defaultValue: [],
    filter: (requests = [], values = []) =>
      commonFilter({
        requests,
        values,
        key: 'regionId',
      }),
  },
  [streets]: {
    serialize: serializes.array,
    deserialize: deserializes.numberArray,
    name: streets,
    defaultValue: [],
    filter: (requests = [], values = []) =>
      commonFilter({
        requests,
        values,
        key: 'streetId',
      }),
  },
  [houses]: {
    serialize: serializes.array,
    deserialize: deserializes.numberArray,
    name: houses,
    defaultValue: [],
    filter: (requests = [], values = []) =>
      commonFilter({
        requests,
        values,
        key: 'houseId',
      }),
  },
  [flats]: {
    serialize: serializes.array,
    deserialize: deserializes.numberArray,
    name: flats,
    defaultValue: [],
    filter: (requests = [], values = []) =>
      commonFilter({
        requests,
        values,
        key: 'addressId',
      }),
  },
  [marks]: {
    serialize: serializes.array,
    deserialize: deserializes.numberArray,
    name: marks,
    defaultValue: [],
    filter: (requests = [], values = []) => {
      if (values.length === 0) {
        return requests;
      }

      const filteredRequests = requests.filter((request) =>
        values.find((markId) => {
          const mark = marksMap.get(markId);

          return mark ? mark.has(request) : false;
        })
      );

      return filteredRequests;
    },
  },
  [applicants]: {
    serialize: serializes.array,
    deserialize: deserializes.numberArray,
    name: applicants,
    defaultValue: [],
    filter: (requests = [], values = []) => {
      if (values.length === 0) {
        return requests;
      }

      const filteredRequests = requests.filter((request) =>
        values.some((value) => {
          if (anonymousApplicantData.has(value)) {
            return anonymousApplicantData.get(value).has(request);
          }

          return request.clientId === value;
        })
      );

      return filteredRequests;
    },
  },
  [ratings]: {
    serialize: serializes.array,
    deserialize: deserializes.stringArray,
    name: ratings,
    defaultValue: [],
    filter: (requests = [], values = []) =>
      commonFilter({
        requests,
        values,
        key: 'rating',
        valueType: 'string',
      }),
  },
  [statusChangePeriod]: {
    serialize: serializes.array,
    deserialize: deserializes.numberArray,
    name: statusChangePeriod,
    defaultValue: [],
    filter: (requests = [], values = []) => {
      if (values.length === 0) {
        return requests;
      }

      const [value] = values;

      const period = statusChangePeriodsMap.get(String(value));

      if (!period) {
        return requests;
      }

      const filteredRequests = requests.filter((request) => {
        const diff = dspxDiffDays(0, request.statusChangeDateTime);
        return diff >= period.min && diff <= period.max;
      });

      return filteredRequests;
    },
  },
  [expiredPeriod]: {
    serialize: serializes.array,
    deserialize: deserializes.numberArray,
    name: expiredPeriod,
    defaultValue: [],
    filter: (requests = [], values = []) => {
      if (values.length === 0) {
        return requests;
      }

      const [value] = values;

      const period = expiredPeriodsMap.get(String(value));

      if (!period) {
        return requests;
      }

      const filteredRequests = requests.filter((request) => {
        const diff = request.doneDate
          ? dspxDiffDays(request.doneDate, request.executeTime)
          : dspxDiffDays(0, request.executeTime);

        return diff >= period.min && diff <= period.max;
      });

      return filteredRequests;
    },
  },
  [creationMethods]: {
    serialize: serializes.array,
    deserialize: deserializes.numberArray,
    name: creationMethods,
    defaultValue: [],
    filter: (requests = [], values = []) =>
      commonFilter({
        requests,
        values,
        key: 'creationMethod.id',
        valueType: 'number',
      }),
  },
  [priorities]: {
    serialize: serializes.array,
    deserialize: deserializes.numberArray,
    name: priorities,
    defaultValue: [],
    filter: (requests = [], values = []) =>
      commonFilter({
        requests,
        values,
        key: 'priority.id',
        valueType: 'number',
      }),
  },
};
